/**
 * Firefly home clusters per backyard — normalized world coordinates.
 *
 * Hubert bounds use desktop authored prop anchors from
 * `jacksonvilleBackyardProps.ts` (world aspect 1672×941).
 */

import { BACKYARD_MASTER_DESKTOP_ASPECT } from '@/world-prototype/camera/backyardPresentation';

export interface FireflyHomeZone {
  x: number;
  y: number;
  /** Loose cluster radius in world-width units. */
  spread: number;
}

/** Rectangular ambient home region — fireflies fill the whole patch. */
export interface FireflyHomeBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface FireflyScatterBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface FireflyYardConfig {
  yardId: string;
  home: FireflyHomeZone;
  /** When set, fireflies spawn and drift throughout this whole region. */
  homeBounds?: FireflyHomeBounds;
  count: number;
  scatterBounds: FireflyScatterBounds;
}

/** Hubert desktop shed — anchor for firefly home rectangle (see BACKYARD_PROP_DEFS `shed`). */
export const HUBERT_FIREFLY_SHED_REFERENCE_DESKTOP = {
  anchorX: 0.81856,
  anchorY: 0.53834,
  widthFrac: 0.1517,
  /** Ground anchor minus half width (`translate(-50%, -100%)`). */
  leftEdgeX: 0.81856 - 0.1517 / 2,
} as const;

/** Hubert desktop tree-right — right edge of open grass before the shed patch. */
export const HUBERT_FIREFLY_TREE_RIGHT_REFERENCE_DESKTOP = {
  anchorX: 0.59066,
  anchorY: 0.40503,
  widthFrac: 0.1208,
  rightEdgeX: 0.59066 + 0.1208 / 2,
} as const;

/**
 * Hubert firefly home rectangle — open upper-right grass LEFT of the shed.
 * Explicit world fractions (desktop 1672×941). Adjusted from the prior
 * 0.65906–0.73471 × 0.335–0.448 zone: +right, +up, wider toward shed edge.
 */
export const HUBERT_FIREFLY_HOME_BOUNDS: FireflyHomeBounds = {
  minX: 0.652,
  maxX: 0.741,
  minY: 0.321,
  maxY: 0.433,
};

/** World plate the Hubert bounds were measured against (desktop master). */
export const HUBERT_FIREFLY_WORLD_ASPECT = BACKYARD_MASTER_DESKTOP_ASPECT;

/**
 * Hubert — explicit home rectangle in upper-right grass (not a semantic guess).
 */
const HUBERT: FireflyYardConfig = {
  yardId: 'hubert',
  home: {
    x: (HUBERT_FIREFLY_HOME_BOUNDS.minX + HUBERT_FIREFLY_HOME_BOUNDS.maxX) * 0.5,
    y: (HUBERT_FIREFLY_HOME_BOUNDS.minY + HUBERT_FIREFLY_HOME_BOUNDS.maxY) * 0.5,
    spread: 0,
  },
  homeBounds: HUBERT_FIREFLY_HOME_BOUNDS,
  count: 28,
  scatterBounds: { minX: 0.2, maxX: 0.88, minY: 0.5, maxY: 0.8 },
};

/**
 * Holly Springs — open grass on the right, past the swingset slide and above
 * the AC pad near the fence. Fills the whole patch as one loose cluster.
 */
const RALEIGH: FireflyYardConfig = {
  yardId: 'raleigh',
  home: { x: 0.855, y: 0.7, spread: 0.08 },
  homeBounds: { minX: 0.756, maxX: 0.93, minY: 0.57, maxY: 0.83 },
  count: 28,
  scatterBounds: { minX: 0.22, maxX: 0.86, minY: 0.48, maxY: 0.78 },
};

/** Spanish Fork — open bowl, single loose cluster near mid-yard. */
const SPANISH_FORK: FireflyYardConfig = {
  yardId: 'spanish-fork',
  home: { x: 0.552, y: 0.58, spread: 0.036 },
  count: 14,
  scatterBounds: { minX: 0.2, maxX: 0.84, minY: 0.45, maxY: 0.78 },
};

const YARDS: Readonly<Record<string, FireflyYardConfig>> = {
  hubert: HUBERT,
  raleigh: RALEIGH,
  'spanish-fork': SPANISH_FORK,
};

export function fireflyConfigForYard(
  yardId: string | null | undefined,
): FireflyYardConfig | null {
  if (!yardId) return null;
  return YARDS[yardId] ?? null;
}

export function fireflyCountForYard(yardId: string): number {
  return fireflyConfigForYard(yardId)?.count ?? 0;
}

export function fireflyHomeCenter(config: FireflyYardConfig): {
  x: number;
  y: number;
} {
  if (config.homeBounds) {
    return {
      x: (config.homeBounds.minX + config.homeBounds.maxX) * 0.5,
      y: (config.homeBounds.minY + config.homeBounds.maxY) * 0.5,
    };
  }
  return { x: config.home.x, y: config.home.y };
}
